/**
 * Material Data Hook
 * Specialized hook for fetching material-related data
 * Built on top of useApi for consistency
 */

import { useMemo } from 'react';
import useApi from './useApi';
import { materialService } from '../services';
import { formatCurrency, formatPerUnit, formatUnit } from '../utils/formatUnit';

/**
 * Utility function to format date from ISO (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss) to display format (DD.MM.YYYY)
 */
const formatDate = (isoDate) => {
  if (!isoDate) return '';

  // Handle both ISO datetime (2025-04-26T00:00:00+00:00) and date-only (2025-04-26) formats
  const dateOnly = isoDate.split('T')[0]; // Extract date part before 'T'
  const [year, month, day] = dateOnly.split('-');

  return `${day}.${month}.${year}`;
};

/**
 * Utility function to format price with currency
 */
const formatPrice = (value, currency, unit) => {
  if (value === null || value === undefined) return '';
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return '';

  // Ohne Waehrung nur die Einheit (z. B. "1.234,00 GWh"), "n/a" zaehlt als leer
  const suffix = formatPerUnit(currency, unit);

  return `${numericValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${suffix ? ` ${suffix}` : ''}`;
};

/**
 * Hook to fetch all materials list
 * Transforms API data to match frontend expectations
 */
export const useAllMaterials = (options = {}) => {
  const lightweight = options.lightweight === true;
  const slim = options.slim === true;
  const fetcher = slim
    ? materialService.getAllMaterialsSlim
    : lightweight
      ? materialService.getCompareOptions
      : materialService.getAllMaterials;
  const cacheKey = slim ? 'materials-list-slim' : lightweight ? 'materials-compare-options' : 'materials-list';
  const result = useApi(fetcher, {
    ...options,
    cacheKey,
    cache: options.cache !== false,
    cacheTTL: options.cacheTTL || 5 * 60 * 1000,
    context: { module: 'Materials', endpoint: slim ? 'listSlim' : lightweight ? 'compareOptions' : 'list' },
  });

  // Transform data to match frontend expectations
  const transformedData = useMemo(() => {
    if (!result.data) return null;

    return result.data.map(material => ({
      id: material.id,
      code: material.code,
      name: material.name,
      description: material.description,
      last_value: material.last_value,
      value_unit: material.value_unit,
      currency: material.currency,
      last_date: material.last_date,

      price: formatPrice(material.last_value, material.currency, material.value_unit),
      date: formatDate(material.last_date),

      // slim mode returns chart_values (number array); full mode extracts from recent_chart_data
      chartData: material.chart_values || material.recent_chart_data?.map(item => item.value) || [],

      recent_chart_data: material.recent_chart_data || [],
    }));
  }, [result.data]);

  return {
    ...result,
    data: transformedData,
  };
};

/**
 * Hook to fetch all sectors
 */
export const useSectors = (options = {}) => {
  return useApi(
    () => materialService.getSectors(),
    {
      ...options,
      cacheKey: 'sectors-list',
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 30 * 60 * 1000, // 30 minutes (sectors don't change often)
      context: { module: 'Materials', endpoint: 'sectors' },
    }
  );
};

/**
 * Hook to fetch top sectors for authenticated user
 * Returns personalized sectors based on user-sector scores
 *
 * @param {Object} options - Hook options
 * @param {number} options.limit - Number of sectors to return (default 4, max 10)
 *
 * Returns data in format:
 * Array<{
 *   sector_id: number,
 *   name: string,
 *   image: string,
 *   score: number
 * }>
 */
export const useTopSectors = (options = {}) => {
  const { limit = 4, ...restOptions } = options;

  const result = useApi(
    () => materialService.getTopSectors({ limit }),
    {
      ...restOptions,
      cacheKey: `top-sectors-${limit}`,
      cache: restOptions.cache !== false,
      cacheTTL: restOptions.cacheTTL || 10 * 60 * 1000, // 10 minutes (personalized data changes more frequently)
      dependencies: [limit],
      context: { module: 'Materials', endpoint: 'topSectors', limit },
    }
  );

  // Transform data to match the format used in ChartsOverview
  // Map sector_id to id for consistency with existing code
  const transformedData = useMemo(() => {
    if (!result.data) return null;

    return result.data.map(sector => ({
      id: sector.sector_id,
      name: sector.name,
      image: sector.image,
      score: sector.score,
    }));
  }, [result.data]);

  return {
    ...result,
    data: transformedData,
  };
};

/**
 * Hook to fetch recently viewed materials for authenticated user
 * Returns materials that the user has recently accessed
 *
 * @param {Object} options - Hook options
 * @param {number} options.limit - Number of materials to return (optional)
 *
 * Returns data in format:
 * Array<{
 *   resource_type: string,
 *   id: number,
 *   code: string,
 *   name: string,
 *   viewed_at: string,
 *   time_series: Array<{ year: number, value: number, unit: string }>
 * }>
 */
export const useRecentViews = (options = {}) => {
  const { limit, ...restOptions } = options;

  const result = useApi(
    () => materialService.getRecentViews(limit ? { limit } : {}),
    {
      ...restOptions,
      cacheKey: limit ? `recent-views-${limit}` : 'recent-views',
      cache: restOptions.cache !== false,
      cacheTTL: restOptions.cacheTTL || 2 * 60 * 1000, // 2 minutes (recent views change frequently)
      dependencies: [limit],
      context: { module: 'Materials', endpoint: 'recentViews', limit },
    }
  );

  // Transform data to match frontend expectations
  const transformedData = useMemo(() => {
    if (!result.data) return null;

    return result.data.map(material => {
      // Extract latest value and unit from time_series
      const latestData = material.time_series?.[material.time_series.length - 1];
      const latestCurrency = formatCurrency(latestData?.currency);
      const latestUnit = formatUnit(latestData?.unit) ? String(latestData.unit).trim() : '';

      return {
        id: material.id,
        code: material.code,
        name: material.name,
        viewed_at: material.viewed_at,
        // Convert time_series values to chartData array for miniChart rendering
        chartData: material.time_series?.map(item => item.value) || [],
        // Keep original time_series for reference
        time_series: material.time_series || [],
        // Extract latest value for price display
        last_value: latestData?.value ?? null,
        value_unit: latestUnit || null,
        currency: latestCurrency || null,
        // Format price display
        price: formatPrice(latestData?.value, latestCurrency, latestUnit) || 'N/A',
        // Format date from viewed_at
        date: material.viewed_at ? formatDate(material.viewed_at.split('T')[0]) : null,
      };
    });
  }, [result.data]);

  return {
    ...result,
    data: transformedData,
  };
};

/**
 * Hook to fetch materials by sector ID
 * Transforms API data to include formatted dates and currency display
 */
export const useSectorMaterials = (sectorId, options = {}) => {
  const defaultTimeframe = (options.timeframe || '1m').toLowerCase();
  const result = useApi(
    () => materialService.getSectorMaterials(sectorId),
    {
      ...options,
      cacheKey: `sector-materials-${sectorId}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 10 * 60 * 1000, // 10 minutes
      dependencies: [sectorId],
      context: { module: 'Sectors', endpoint: 'materials', sectorId },
    }
  );

  // Transform data to add formatted fields for frontend display
  const transformedData = useMemo(() => {
    if (!result.data) return null;

    const formatEntry = (entry = {}, material = {}) => {
      const currency = formatCurrency(entry?.currency) || formatCurrency(material.currency) || null;
      const unitRaw = entry?.unit || material.unit;
      const unit = formatUnit(unitRaw) ? String(unitRaw).trim() : null;
      const lastVal = entry?.last_value ?? material.last_value ?? null;
      const highVal = entry?.high ?? material.high ?? null;
      const lowVal = entry?.low ?? material.low ?? null;
      const changePct = entry?.change_pct ?? material.change_pct ?? null;
      const dateIso = entry?.last_date || material.last_date || null;

      const toDisplay = (val) =>
        val !== null && val !== undefined
          ? val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : '—';

      return {
        ...entry,
        last_value: lastVal,
        high: highVal,
        low: lowVal,
        change_pct: changePct,
        last_date: dateIso,
        dateDisplay: formatDate(dateIso),
        valueDisplay: toDisplay(lastVal),
        highDisplay: toDisplay(highVal),
        lowDisplay: toDisplay(lowVal),
        changeDisplay:
          changePct !== null && changePct !== undefined
            ? `${changePct > 0 ? '+' : ''}${changePct.toFixed(2)} %`
            : 'N/A',
        currencyUnit: formatPerUnit(currency, unit),
        currency,
        unit,
      };
    };

    return {
      lists: result.data.lists?.map(list => ({
        ...list,
        materials: list.materials?.map(material => {
          const formattedTimeframes = {};
          Object.entries(material.timeframes || {}).forEach(([tf, entry]) => {
            formattedTimeframes[tf.toLowerCase()] = formatEntry(entry || {}, material);
          });

          const fallback =
            formattedTimeframes[defaultTimeframe] ||
            formattedTimeframes['1m'] ||
            formattedTimeframes['3m'] ||
            formattedTimeframes['max'] ||
            formatEntry({}, material);

          return {
            ...material,
            formattedTimeframes,
            date: fallback.dateDisplay,
            value: fallback.valueDisplay,
            highDisplay: fallback.highDisplay,
            lowDisplay: fallback.lowDisplay,
            currencyUnit: fallback.currencyUnit,
            change: fallback.changeDisplay,
            change_pct: fallback.change_pct,
          };
        }) || []
      })) || []
    };
  }, [result.data, defaultTimeframe]);

  return {
    ...result,
    data: transformedData,
  };
};

/**
 * Hook to fetch general material information
 */
export const useMaterialGeneral = (materialCode, options = {}) => {
  return useApi(
    () => materialService.getGeneral(materialCode),
    {
      ...options,
      cacheKey: `material-general-${materialCode}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 5 * 60 * 1000, // 5 minutes
      dependencies: [materialCode],
      context: { module: 'Materials', endpoint: 'general' },
    }
  );
};

/**
 * Hook to fetch chart overview data
 */
export const useMaterialChartOverview = (materialCode, options = {}) => {
  return useApi(
    () => materialService.getChartOverview(materialCode),
    {
      ...options,
      cacheKey: `material-chart-overview-${materialCode}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 10 * 60 * 1000, // 10 minutes (historical data changes rarely)
      dependencies: [materialCode],
      context: { module: 'Materials', endpoint: 'chartOverview' },
    }
  );
};

/**
 * Hook to fetch chart series with timeframe
 * params.currency / params.unit (optional) gehen in den Request und in den
 * Cache-Key, damit umgerechnete und Originalreihen getrennt gecacht werden.
 */
export const useMaterialChartSeries = (materialCode, params = {}, options = {}) => {
  const paramsString = JSON.stringify(params);

  return useApi(
    () => materialService.getChartSeries(materialCode, params),
    {
      ...options,
      cacheKey: `material-chart-series-${materialCode}-${paramsString}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 2 * 60 * 1000, // 2 minutes (more recent data)
      dependencies: [materialCode, paramsString],
      context: { module: 'Materials', endpoint: 'chartSeries', params },
    }
  );
};

// /reference/* liefert Listen; tolerant gegenueber { items: [...] }.
const toList = (payload, key) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[key])) return payload[key];
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

/**
 * Waehrungen fuer die Chart-Umrechnung (/reference/currencies).
 * Selten geaendert und oeffentlich: lange im Speicher halten, der Browser
 * revalidiert zusaetzlich per ETag.
 */
export const useReferenceCurrencies = (options = {}) => {
  const result = useApi(
    () => materialService.getReferenceCurrencies(),
    {
      ...options,
      cacheKey: 'reference-currencies',
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 6 * 60 * 60 * 1000,
      context: { module: 'Reference', endpoint: 'currencies' },
    }
  );
  const data = useMemo(() => (result.data ? toList(result.data, 'currencies') : null), [result.data]);
  return { ...result, data };
};

/**
 * Einheiten fuer die Chart-Umrechnung (/reference/units).
 */
export const useReferenceUnits = (options = {}) => {
  const result = useApi(
    () => materialService.getReferenceUnits(),
    {
      ...options,
      cacheKey: 'reference-units',
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 6 * 60 * 60 * 1000,
      context: { module: 'Reference', endpoint: 'units' },
    }
  );
  const data = useMemo(() => (result.data ? toList(result.data, 'units') : null), [result.data]);
  return { ...result, data };
};

/**
 * Hook to fetch assessment data
 */
export const useMaterialAssessment = (materialCode, options = {}) => {
  return useApi(
    () => materialService.getAssessment(materialCode),
    {
      ...options,
      cacheKey: `material-assessment-${materialCode}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 30 * 60 * 1000, // 30 minutes (rarely changes)
      dependencies: [materialCode],
      context: { module: 'Materials', endpoint: 'assessment' },
    }
  );
};

/**
 * Hook to fetch availability data
 */
export const useMaterialAvailability = (materialCode, options = {}) => {
  return useApi(
    () => materialService.getAvailability(materialCode),
    {
      ...options,
      cacheKey: `material-availability-${materialCode}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 15 * 60 * 1000, // 15 minutes
      dependencies: [materialCode],
      context: { module: 'Materials', endpoint: 'availability' },
    }
  );
};

/**
 * Hook to fetch country production data
 */
export const useMaterialCountryProduction = (materialCode, options = {}) => {
  return useApi(
    () => materialService.getCountryProduction(materialCode),
    {
      ...options,
      cacheKey: `material-country-production-${materialCode}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 30 * 60 * 1000, // 30 minutes
      dependencies: [materialCode],
      context: { module: 'Materials', endpoint: 'countryProduction' },
    }
  );
};

/**
 * Hook to fetch market stability data
 */
export const useMaterialMarketStability = (materialCode, options = {}) => {
  return useApi(
    () => materialService.getMarketStability(materialCode),
    {
      ...options,
      cacheKey: `material-market-stability-${materialCode}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 30 * 60 * 1000, // 30 minutes
      dependencies: [materialCode],
      context: { module: 'Materials', endpoint: 'marketStability' },
    }
  );
};

/**
 * Hook to fetch need projection data
 */
export const useMaterialNeedProjection = (materialCode, options = {}) => {
  return useApi(
    () => materialService.getNeedProjection(materialCode),
    {
      ...options,
      cacheKey: `material-need-projection-${materialCode}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 60 * 60 * 1000, // 1 hour (long-term forecast)
      dependencies: [materialCode],
      context: { module: 'Materials', endpoint: 'needProjection' },
    }
  );
};

/**
 * Hook to fetch recent daily prices
 */
export const useMaterialRecentDaily = (materialCode, options = {}) => {
  return useApi(
    () => materialService.getRecentDaily(materialCode),
    {
      ...options,
      cacheKey: `material-recent-daily-${materialCode}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 1 * 60 * 1000, // 1 minute (very recent data)
      dependencies: [materialCode],
      context: { module: 'Materials', endpoint: 'recentDaily' },
    }
  );
};

/**
 * Hook to fetch rolling average data
 */
export const useMaterialRollingAverage = (materialCode, options = {}) => {
  return useApi(
    () => materialService.getRollingAverage(materialCode),
    {
      ...options,
      cacheKey: `material-rolling-average-${materialCode}`,
      cache: options.cache !== false,
      cacheTTL: options.cacheTTL || 5 * 60 * 1000, // 5 minutes
      dependencies: [materialCode],
      context: { module: 'Materials', endpoint: 'rollingAverage' },
    }
  );
};

/**
 * Comprehensive hook to fetch ALL material data at once
 * Useful for initial page load or when all data is needed
 *
 * Returns an object with all data endpoints
 */
export const useMaterialAllData = (materialCode, options = {}) => {
  const general = useMaterialGeneral(materialCode, options);
  const chartOverview = useMaterialChartOverview(materialCode, options);
  const assessment = useMaterialAssessment(materialCode, options);
  const availability = useMaterialAvailability(materialCode, options);
  const countryProduction = useMaterialCountryProduction(materialCode, options);
  const marketStability = useMaterialMarketStability(materialCode, options);
  const needProjection = useMaterialNeedProjection(materialCode, options);
  const recentDaily = useMaterialRecentDaily(materialCode, options);
  const rollingAverage = useMaterialRollingAverage(materialCode, options);

  // Aggregate loading and error states
  const loading = useMemo(() => {
    return (
      general.loading ||
      chartOverview.loading ||
      assessment.loading ||
      availability.loading ||
      countryProduction.loading ||
      marketStability.loading ||
      needProjection.loading ||
      recentDaily.loading ||
      rollingAverage.loading
    );
  }, [
    general.loading,
    chartOverview.loading,
    assessment.loading,
    availability.loading,
    countryProduction.loading,
    marketStability.loading,
    needProjection.loading,
    recentDaily.loading,
    rollingAverage.loading,
  ]);

  const error = useMemo(() => {
    return (
      general.error ||
      chartOverview.error ||
      assessment.error ||
      availability.error ||
      countryProduction.error ||
      marketStability.error ||
      needProjection.error ||
      recentDaily.error ||
      rollingAverage.error
    );
  }, [
    general.error,
    chartOverview.error,
    assessment.error,
    availability.error,
    countryProduction.error,
    marketStability.error,
    needProjection.error,
    recentDaily.error,
    rollingAverage.error,
  ]);

  // Refetch all data
  const refetchAll = () => {
    general.refetch();
    chartOverview.refetch();
    assessment.refetch();
    availability.refetch();
    countryProduction.refetch();
    marketStability.refetch();
    needProjection.refetch();
    recentDaily.refetch();
    rollingAverage.refetch();
  };

  return {
    general,
    chartOverview,
    assessment,
    availability,
    countryProduction,
    marketStability,
    needProjection,
    recentDaily,
    rollingAverage,
    loading,
    error,
    refetchAll,
  };
};

/**
 * Hook to fetch multiple materials for comparison
 * Fetches the same endpoint for multiple materials in parallel
 */
export const useMaterialComparison = (materialCodes = [], fetchHook, options = {}) => {
  const results = useMemo(() => {
    return materialCodes.map(code => fetchHook(code, options));
  }, [materialCodes.join(','), options]); // eslint-disable-line react-hooks/exhaustive-deps

  const loading = useMemo(() => {
    return results.some(result => result.loading);
  }, [results]);

  const error = useMemo(() => {
    return results.find(result => result.error)?.error || null;
  }, [results]);

  const data = useMemo(() => {
    return results.map(result => result.data);
  }, [results]);

  const refetchAll = () => {
    results.forEach(result => result.refetch());
  };

  return {
    data,
    loading,
    error,
    results,
    refetchAll,
  };
};

export default useMaterialAllData;
